<template>
  <div class="site-footer critical-component-clear">
    <div class="container">
      <div class="site-footer__connect">
        <h2
          class="site-footer__header"
          v-text="$string('general.newsletter.header')"
        />

        <div
          v-if="newsletter.subheader"
          class="site-footer__subheader"
          v-html="newsletter.subheader"
        />

        <form
          id="SiteFooterNewsletter"
          class="site-footer__join"
          @submit.prevent="handleSubscribe"
        >
          <div
            v-show="!newsletterSuccess"
            class="site-footer__join-fields"
          >
            <div class="site-footer__join-email">
              <label
                class="visually-hidden"
                for="FooterNewsletterEmail"
                v-text="$string('general.newsletter.email')"
              />

              <input
                id="FooterNewsletterEmail"
                v-model="email"
                name="email"
                :placeholder="$string('general.newsletter.email')"
                required
                type="email"
              >

              <span
                v-if="validationErrors.email"
                class="form-group__error"
                v-text="$string('general.newsletter_popup.enter_valid_email')"
              />
            </div>

            <div
              v-if="newsletter.showPhoneField"
              class="site-footer__join-sms"
            >
              <input
                id="FooterNewsletterSms"
                class="checkbox__input"
                type="checkbox"
                @change="onCheckboxChange($event)"
              >

              <label
                for="FooterNewsletterSms"
                class="checkbox__label body-2"
                v-text="$string('general.newsletter.sms_label')"
              />
            </div>

            <div
              v-show="smsConsent"
              class="site-footer__join-phone"
            >
              <label
                class="visually-hidden"
                for="FooterNewsletterPhone"
                v-text="$string('general.newsletter.phone')"
              />

              <input
                id="FooterNewsletterPhone"
                v-model="phone"
                name="phone"
                :placeholder="$string('general.newsletter.phone')"
                type="tel"
              >

              <span
                v-if="validationErrors.phone"
                class="form-group__error"
                v-text="$string('general.newsletter_popup.enter_valid_phone')"
              />
            </div>
          </div>

          <div
            v-show="!newsletterSuccess"
            class="site-footer__join-actions"
          >
            <btn
              type="submit"
              :label="$string('general.newsletter.submit')"
              :class="{
                'right-aligned': !smsConsent,
                'right-aligned-error': !smsConsent && validationErrors.email,
                'has-phone-field': newsletter.showPhoneField
              }"
              modifiers="text"
              icon-only
            >
              <template #icon>
                <icon-arrow-right />
              </template>
            </btn>
          </div>

          <div class="site-footer__join-messages body-2">
            <div
              v-show="newsletterSuccess"
              v-text="$string('general.newsletter.confirmation')"
            />

            <div
              v-show="newsletterFailed"
              class="form-group__error"
              v-text="errorMessage"
            />
          </div>
        </form>

        <site-footer-socials
          class="site-footer__socials-desktop"
          :socials="socials"
        />
      </div>

      <div
        v-if="!isEmpty(menus)"
        class="site-footer__nav"
      >
        <div
          v-for="(menu, index) of menus"
          :key="index"
          class="site-footer__linklist-wrapper"
        >
          <h4
            class="site-footer__linklist-header"
            v-text="menu.name"
          />
          <ul
            class="site-footer__linklist"
          >
            <li
              v-for="(link, linkIndex) of menu.links"
              :key="linkIndex"
            >
              <a
                class="site-footer__link"
                :href="link.url"
                v-text="link.title"
              />
            </li>
          </ul>
        </div>
      </div>
      <site-footer-socials
        :socials="socials"
        class="site-footer__socials-mobile"
      />
    </div>

    <div class="site-footer__copyright">
      <ul class="container site-footer__copyright-linklist">
        <li>
          <div class="site-footer__link">
            <span
              class="site-footer__copyright-icon"
              v-text="`&copy`"
            />
            <span v-text="`${new Date().getFullYear()} ${shopName}`" />
          </div>
        </li>

        <li>
          <slot name="currency" />
        </li>

        <li
          v-for="(link, linkIndex) of bottomMenu.links"
          :key="linkIndex"
        >
          <a
            class="site-footer__link"
            :href="link.url"
            v-text="link.title"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Site footer
 * -----------------------------------------------------------------------------
 * Site footer component with accordion display of links.
 *
 * @param {String} shopName
 * @param {Object} socials - social media links
 * @param {Array} menus - main link lists
 * @param {Object} newsletter - sign up form settings
 * @param {Object} bottomMenu - link list displayed with copyright
 * @param {String} listId - Klaviyo email list id
 * @param {String} smsListId - Klaviyo SMS list id
 * @param {String} internationalListId - Klaviyo email list id - international
 * @param {String} internationalSmsListId - Klaviyo SMS list id - international
 */

import { isEmpty } from '~/helpers/general'
import Btn from '~global/btn/btn'
import IconArrowRight from '~icons/direction/arrow/right.svg'
import SiteFooterSocials from './site-footer-socials'

export default {
  name: 'SiteFooter',

  components: {
    Btn,
    IconArrowRight,
    SiteFooterSocials,
  },

  props: {
    shopName: {
      type: String,
      default: '',
    },
    socials: {
      type: Object,
      default: () => ({}),
    },
    menus: {
      type: Array,
      default: () => [],
    },
    newsletter: {
      type: Object,
      default: () => ({
        subheader: '',
        showPhoneField: false,
      }),
    },
    bottomMenu: {
      type: Object,
      default: () => ({}),
    },
    listId: {
      type: String,
      default: '',
    },
    smsListId: {
      type: String,
      default: '',
    },
    internationalListId: {
      type: String,
      default: '',
    },
    internationalSmsListId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      email: '',
      phone: '',
      newsletterSuccess: false,
      newsletterFailed: false,
      errorMessage: '',
      smsConsent: false,
      validationErrors: {
        email: false,
        phone: false,
      },
    }
  },

  methods: {

    /**
     * Checks for empty array
     * @returns {Boolean}
     */
    isEmpty,

    /**
     * Toggle the smsConsent param on checkbox click
     * @param {Object} event - click event
     */
    onCheckboxChange(event) {
      this.smsConsent = event.target.checked
    },

    /**
     * Check if entered email address is valid.
     * @returns {Boolean}
     */
    validateEmail() {
      if ((/^\w+(?<address>[.-]?\w+)*@\w+(?<tags>[.-]?\w+)*(?<domain>\.\w{2,3})+$/).test(this.email)) {
        return true
      }

      return false
    },

    /**
     * Check if entered phone is valid or blank.
     * @returns {Boolean}
     */
    validatePhone() {
      if ((/^(?<areaCode>\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/).test(this.phone) ||
        (/^\s*$/).test(this.phone)) {
        return true
      }

      return false
    },

    /**
     * Check if email and phone fields are valid.
     * @returns {Boolean}
     */
    isInputValid() {
      if (!this.validateEmail()) {
        this.validationErrors.email = true
        return false
      }

      if (!this.validatePhone()) {
        this.validationErrors.phone = true
        return false
      }

      this.validationErrors.email = false
      this.validationErrors.phone = false

      return true
    },

    /**
     * Build Klaviyo request object.
     * @param {Node} form
     * @returns {Object} requestParams
     */
    buildKlaviyoRequest(form) {
      let listId = ''
      if (!this.smsConsent || !form.querySelector('[name="phone"]').value) {
        listId = window.Shopify.country.toLowerCase() === 'us' ? this.listId : this.internationalListId
      } else {
        listId = window.Shopify.country.toLowerCase() === 'us' ? this.smsListId : this.internationalSmsListId
      }

      const request = {
        g: listId,
        email: form.querySelector('[name="email"]').value,
        phone_number: form.querySelector('[name="phone"]').value,
        sms_consent: this.smsConsent,
        $fields: '$consent, $source',
        $list_fields: '$consent',
        $source: 'Newsletter',
      }

      let requestParams = ''
      for (const [key, value] of Object.entries(request)) {
        requestParams += `${key}=${value}&`
      }
      requestParams = requestParams.replace(/&$/, '')

      return requestParams
    },

    /**
     * Make Klaviyo API call request.
     * @param {Object} requestParams
     */
    makeKlaviyoRequest(requestParams) {
      fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
        async: this,
        cache: 'no-cache',
        crossDomain: true,
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(requestParams),
      })
        .then((response) => {
          if (response.ok) {
            this.newsletterSuccess = true
            this.newsletterFailed = false
          } else {
            this.newsletterSuccess = false
            this.newsletterFailed = true
            this.errorMessage = response.json().errors.join()
          }
        })
        .catch((error) => {
          throw new Error(error)
        })
    },

    /**
     * Subscribe email address to Klaviyo newsletter list.
     * @param {Event} event
     */
    handleSubscribe(event) {
      if (!this.isInputValid()) {
        return
      }

      const requestParams = this.buildKlaviyoRequest(event.target)

      this.makeKlaviyoRequest(requestParams)
    },
  },
}
</script>

<style lang="scss">
@import './site-footer';
</style>
